import React, { useState, useEffect, useCallback } from "react";
import { Alert, Table } from "react-bootstrap";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import TableFooter from "../../../components/TableFooter.jsx";
import genericTranslations from "../../../utils/genericTranslations.js";
import { formatLocalDate } from "../../../utils/utils.js";
import { selectNotifications, isLoadingNotifications } from "../selectors";
import { fetchNotifications } from "../thunks";

const messages = {
  title: {
    id: "modules.SoftwarePackage.components.SoftwareNotifications.title",
    description: "Notification message",
    defaultMessage: "Message",
  },
  editedAt: {
    id: "modules.SoftwarePackage.components.SoftwareNotifications.editedAt",
    description: "Edited at date",
    defaultMessage: "Last edited at",
  },
  pushNotification: {
    id: "modules.SoftwarePackage.components.SoftwareNotifications.pushNotification",
    description: "Push notification status",
    defaultMessage: "Push notification",
  },
};

const SoftwareNotifications = () => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [error, setError] = useState(null);
  const pageSize = 10;

  const notifications = useSelector(selectNotifications);
  const isLoading = useSelector(isLoadingNotifications);

  const fetchData = useCallback(async () => {
    try {
      const pagesFromResponse = await dispatch(
        fetchNotifications({ page, pageSize }),
      );
      setPages(pagesFromResponse);
    } catch (err) {
      console.error("Error fetching notifications:", err);
      setError(err);
      setPage(1);
      setPages(0);
    }
  }, [dispatch, page, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onSelectPage = (newPage) => {
    setPage(newPage);
  };

  const dismissError = () => setError(null);

  const mapNotificationToTableRow = (notification) => (
    <tr key={`message-${notification.id}`}>
      <td className="text-truncate" style={{ maxWidth: "500px" }}>
        {!notification.sendAt ? (
          <Link to={`/message/package-notification/edit/${notification.id}`}>
            {notification.title}
          </Link>
        ) : (
          notification.title
        )}
      </td>
      <td>{notification.tag}</td>
      <td>
        {notification.sendAt !== null
          ? notification.pushNotification
            ? formatMessage(genericTranslations.yes)
            : formatMessage(genericTranslations.no)
          : ""}
      </td>
      <td>{notification.updatedBy}</td>
      <td>{notification.sendAt && formatLocalDate(notification.sendAt)}</td>
    </tr>
  );

  return (
    <div>
      {error && (
        <Alert variant="danger" onClose={dismissError} dismissible>
          <Alert.Heading>
            {formatMessage(genericTranslations.error)}
          </Alert.Heading>
          <p>{error.toString()}</p>
        </Alert>
      )}

      {isLoading && (
        <span className="align-middle">
          {formatMessage(genericTranslations.loadingInProgress)}
        </span>
      )}

      {!isLoading && notifications.length === 0 && (
        <span className="align-middle">
          {formatMessage(genericTranslations.nothingFound)}
        </span>
      )}

      {!isLoading && notifications.length > 0 && (
        <Table responsive>
          <thead>
            <tr>
              <td>{formatMessage(messages.title)}</td>
              <td>Tag</td>
              <td>{formatMessage(messages.pushNotification)}</td>
              <td>{formatMessage(genericTranslations.updatedBy)}</td>
              <td>{formatMessage(messages.editedAt)}</td>
            </tr>
          </thead>
          <tbody>{notifications.map(mapNotificationToTableRow)}</tbody>
        </Table>
      )}

      <TableFooter
        actionPath="/message/package-notification/add"
        onSelectPage={onSelectPage}
        page={page}
        pages={pages}
      />
    </div>
  );
};

export default SoftwareNotifications;
